/**
 * @file
 * Partner Map/List Toggle functionality.
 *
 * Simplified implementation for the partner_map_list paragraph type.
 * Handles toggling between list and map views with Google Maps integration.
 *
 * @param {object} Drupal - The Drupal object.
 * @param {object} drupalSettings - The Drupal settings object.
 */

(function (Drupal, drupalSettings) {
  'use strict';

  /**
   * Partner Map List Toggle behavior.
   */
  Drupal.behaviors.partnerMapListToggle = {
    // Instance tracking.
    initialized: new Set(),
    mapInstances: {},
    markerClusterers: {},
    markers: {},
    infoWindows: {},
    activeMarkers: {},

    // Bulk operation tracking.
    bulkOperationInProgress: {},
    filterChangeTimeouts: {},

    // Google Maps API loading state.
    googleMapsLoaded: false,
    googleMapsLoadPromise: null,
    markerClustererLoaded: false,
    markerClustererLoadPromise: null,

    // Configuration constants.
    MARKER_CONFIG: {
      normal: {
        url: '/themes/custom/tnvacation/img/marker.png',
        size: {width: 58, height: 58},
        anchor: {x: 29, y: 20}
      },
      active: {
        url: '/themes/custom/tnvacation/img/marker_active.png',
        size: {width: 58, height: 58},
        anchor: {x: 29, y: 20}
      },
      cluster: {
        url: '/themes/custom/tnvacation/img/marker_cluster.png',
        size: {width: 58, height: 58},
        anchor: {x: 29, y: 29}
      }
    },
    MAP_DEFAULTS: {
      center: {lat: 35.5175, lng: -86.5804}, // Tennessee center.
      zoom: 7,
      maxZoom: 20,
      clusterRadius: 150,
      desktopPanOffset: 0.4 // 40% offset for desktop panning.
    },
    BREAKPOINTS: {
      mobile: 768
    },

    /**
     * Attach behavior to toggle buttons.
     *
     * @param {HTMLElement} context - The context element.
     * @param {Object} settings - Settings from drupalSettings.
     */
    attach: function (context, settings) {
      const self = this;

      // Find all partner-map-list paragraphs in the context.
      context.querySelectorAll('.partner-map-list[data-paragraph-id]').forEach((paragraph) => {
        const paragraphId = paragraph.getAttribute('data-paragraph-id');

        // Skip if already initialized.
        if (self.initialized.has(paragraphId)) {
          return;
        }

        // Get settings for this paragraph.
        const paragraphSettings = settings.partnerMapListToggle?.[paragraphId];
        if (!paragraphSettings) {
          return;
        }

        // Mark as initialized.
        self.initialized.add(paragraphId);

        // Initialize toggle functionality.
        self.initializeToggle(paragraph, paragraphSettings, paragraphId);
      });
    },

    /**
     * Initialize toggle functionality for a paragraph.
     *
     * @param {HTMLElement} paragraph - The paragraph wrapper element.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {string} paragraphId - The paragraph ID.
     */
    initializeToggle: function (paragraph, settings, paragraphId) {
      const self = this;
      const wrapper = paragraph.querySelector('.partner-map-list__wrapper');
      const mapView = paragraph.querySelector('.partner-map-list__map');
      const listWrapper = paragraph.querySelector('.partner-map-list__list-wrapper');

      if (!wrapper || !mapView || !listWrapper) {
        return;
      }

      // Prevent keyboard and screen reader navigation inside the map container.
      const makeNonFocusable = () => {
        mapView.querySelectorAll(':is(a, button, iframe, input, select, textarea, [tabindex]):not([tabindex="-1"])')
          .forEach(el => el.setAttribute('tabindex', '-1'));
        mapView.querySelectorAll('iframe:not([aria-hidden="true"])')
          .forEach(el => el.setAttribute('aria-hidden', 'true'));
      };
      // Observe childList and tabindex attribute changes.
      new MutationObserver(makeNonFocusable).observe(mapView, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex']
      });
      makeNonFocusable();

      // Use event delegation on the stable paragraph element.
      paragraph.addEventListener('click', (event) => {
        const button = event.target.closest('.partner-map-list__toggle-button');
        if (!button) {
          return;
        }
        event.preventDefault();
        const targetView = button.getAttribute('data-toggle-to');
        self.toggleView(wrapper, mapView, targetView, paragraphId, settings, button);
      });

      // Listen for 'clear' events to track bulk operations.
      const pseudoFiltersContainer = paragraph.querySelector('.pseudo-filters-container');
      if (pseudoFiltersContainer) {
        pseudoFiltersContainer.addEventListener('clear', (event) => {
          if (event.detail === 'start') {
            self.bulkOperationInProgress[paragraphId] = true;
          }
          else if (event.detail === 'end') {
            self.bulkOperationInProgress[paragraphId] = false;
            // Trigger single map reload after bulk operation completes.
            const currentView = wrapper.getAttribute('data-current-view');
            if (currentView === 'map' && self.mapInstances[paragraphId]) {
              self.debouncedFilterChange(paragraph, settings, paragraphId);
            }
          }
        });
      }

      // Listen for filter changes on the exposed form using event delegation.
      // Attach to paragraph element so listeners persist after AJAX updates.
      paragraph.addEventListener('change', (event) => {
        // Check if the change event came from a select element within the exposed form.
        const exposedForm = paragraph.querySelector('.views-exposed-form');
        if (exposedForm && exposedForm.contains(event.target) && event.target.matches('select')) {
          // Skip individual reloads during bulk operations.
          if (self.bulkOperationInProgress[paragraphId]) {
            return;
          }

          // Only reload map if it's currently visible.
          const currentView = wrapper.getAttribute('data-current-view');
          if (currentView === 'map' && self.mapInstances[paragraphId]) {
            // Use debounced method for consolidated reload.
            self.debouncedFilterChange(paragraph, settings, paragraphId);
          }
        }
      });

      // Listen for AJAX updates to reload map data if filters change.
      // This handles cases where the view uses AJAX.
      document.addEventListener('views-ajax-update', (event) => {
        if (self.isRelatedView(event, settings)) {
          self.handleAjaxUpdate(paragraph, settings, paragraphId);
        }
      });
    },

    /**
     * Scroll footer into view using native browser positioning.
     *
     * @param {HTMLElement} footer - The footer element.
     */
    scrollFooter: function (footer) {
      requestAnimationFrame(() => {
        footer.scrollIntoView({behavior: 'instant', block: 'end', inline: 'nearest'});
      });
    },

    /**
     * Toggle between map and list views.
     *
     * @param {HTMLElement} wrapper - The wrapper element.
     * @param {HTMLElement} mapView - The map view container.
     * @param {string} targetView - The view to show ('map' or 'list').
     * @param {string} paragraphId - The paragraph ID.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {HTMLElement} toggleButton - The button that triggered the toggle.
     */
    toggleView: function (wrapper, mapView, targetView, paragraphId, settings, toggleButton) {
      const self = this;
      const paragraph = wrapper.closest('.partner-map-list');
      const isMobile = window.innerWidth < self.BREAKPOINTS.mobile;
      const footer = paragraph.querySelector('.partner-map-list__footer');

      // Update view state. The single button stays in the DOM — focus is never lost.
      wrapper.setAttribute('data-current-view', targetView);

      if (targetView === 'map') {
        mapView.removeAttribute('hidden');
        self.updateToggleButton(toggleButton, 'map');

        if (isMobile && footer) {
          self.scrollFooter(footer);
        }

        // Initialize map if not already done.
        if (!self.mapInstances[paragraphId]) {
          self.initializeMap(mapView, settings, paragraphId);
        }
        else {
          // Map already exists, reload markers with current filter values.
          self.loadMarkers(self.mapInstances[paragraphId], settings, paragraphId);
        }
      }
      else {
        mapView.setAttribute('hidden', '');
        self.updateToggleButton(toggleButton, 'list');

        if (isMobile && footer) {
          self.scrollFooter(footer);
        }
      }
    },

    /**
     * Update toggle button text and attributes for the active view.
     *
     * @param {HTMLElement} button - The toggle button element.
     * @param {string} targetView - The view that is now active ('map' or 'list').
     */
    updateToggleButton: function (button, targetView) {
      if (!button) {
        return;
      }

      const isMap = targetView === 'map';

      // Update data-toggle-to and aria-expanded.
      // The hidden attribute (not CSS) is toggled on the inactive state span
      button.setAttribute('data-toggle-to', isMap ? 'list' : 'map');
      button.setAttribute('aria-expanded', isMap ? 'true' : 'false');
      button.setAttribute('data-analytics-label', isMap ? button.dataset.labelToList : button.dataset.labelToMap);

      const stateToMap = button.querySelector('.partner-map-list__toggle-state--to-map');
      const stateToList = button.querySelector('.partner-map-list__toggle-state--to-list');
      if (stateToMap && stateToList) {
        if (isMap) {
          stateToMap.setAttribute('hidden', '');
          stateToList.removeAttribute('hidden');
        }
        else {
          stateToMap.removeAttribute('hidden');
          stateToList.setAttribute('hidden', '');
        }
      }

      // Guarantees all AT (including NVDA) read only the current state label.
      const visibleText = button.querySelector('.partner-map-list__toggle-state:not([hidden]) .cta-button__text');
      if (visibleText) {
        button.setAttribute('aria-label', visibleText.textContent.trim());
      }
    },

    /**
     * Initialize Google Map.
     *
     * @param {HTMLElement} mapView - The map view container.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {string} paragraphId - The paragraph ID.
     */
    initializeMap: function (mapView, settings, paragraphId) {
      const self = this;
      const mapContainer = mapView.querySelector('[data-map-container]');
      const loadingIndicator = mapView.querySelector('[data-map-loading]');

      if (!mapContainer) {
        return;
      }

      // Show loading indicator.
      if (loadingIndicator) {
        loadingIndicator.style.display = 'block';
      }

      // Load Google Maps API if not already loaded.
      self.loadGoogleMapsApi(settings.googleMapsApiKey)
        .then(() => {
          // Create map instance.
          const map = new google.maps.Map(mapContainer, {
            center: self.MAP_DEFAULTS.center,
            zoom: self.MAP_DEFAULTS.zoom,
            maxZoom: self.MAP_DEFAULTS.maxZoom,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true
          });

          // Store map instance.
          self.mapInstances[paragraphId] = map;

          // Load and display markers.
          self.loadMarkers(map, settings, paragraphId);
        })
        .catch((error) => {
          // Log error for debugging.
          if (window.console && window.console.error) {
            window.console.error('Failed to initialize map:', error);
          }
          if (loadingIndicator) {
            loadingIndicator.textContent = 'Failed to load map. Please try again.';
          }
        });
    },

    /**
     * Load Google Maps API dynamically.
     *
     * @param {string} apiKey - Google Maps API key.
     *
     * @return {Promise} Promise that resolves when API is loaded.
     */
    loadGoogleMapsApi: function (apiKey) {
      const self = this;

      if (window.google?.maps) {
        self.googleMapsLoaded = true;

        if (typeof markerClusterer !== 'undefined') {
          self.markerClustererLoaded = true;
          return Promise.resolve();
        }

        return self.loadMarkerClusterer();
      }

      if (self.googleMapsLoadPromise) {
        return self.googleMapsLoadPromise;
      }

      self.googleMapsLoadPromise = new Promise((resolve, reject) => {
        const callbackName = `initGoogleMaps_${Date.now()}`;

        // Define global callback.
        window[callbackName] = () => {
          delete window[callbackName];

          // Load marker clusterer library.
          self.loadMarkerClusterer()
            .then(() => {
              self.googleMapsLoaded = true;
              resolve();
            })
            .catch((error) => {
              self.googleMapsLoadPromise = null;
              reject(error);
            });
        };

        // Load Google Maps API script.
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          delete window[callbackName];
          self.googleMapsLoadPromise = null;
          reject(new Error('Failed to load Google Maps API'));
        };

        document.head.appendChild(script);
      });

      return self.googleMapsLoadPromise;
    },

    /**
     * Load marker clustering library.
     *
     * @return {Promise} Promise that resolves when library is loaded.
     */
    loadMarkerClusterer: function () {
      const self = this;

      if (typeof markerClusterer !== 'undefined') {
        self.markerClustererLoaded = true;
        return Promise.resolve();
      }

      if (self.markerClustererLoadPromise) {
        return self.markerClustererLoadPromise;
      }

      self.markerClustererLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
        script.async = true;
        script.onload = () => {
          if (typeof markerClusterer === 'undefined') {
            self.markerClustererLoadPromise = null;
            reject(new Error('Failed to initialize marker clustering library'));
            return;
          }

          self.markerClustererLoaded = true;
          resolve();
        };
        script.onerror = () => {
          self.markerClustererLoadPromise = null;
          reject(new Error('Failed to load marker clustering library'));
        };
        document.head.appendChild(script);
      });

      return self.markerClustererLoadPromise;
    },

    /**
     * Load markers from REST endpoint and display on map.
     *
     * @param {google.maps.Map} map - The Google Maps instance.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {string} paragraphId - The paragraph ID.
     */
    loadMarkers: function (map, settings, paragraphId) {
      const self = this;
      const restUrl = self.getFilteredRestUrl(settings.restUrl, paragraphId);

      // Get loading indicator element.
      const mapView = document.querySelector(`[data-paragraph-id="${paragraphId}"] [data-view-type="map"]`);
      const loadingIndicator = mapView?.querySelector('[data-map-loading]');
      const mapContainer = mapView?.querySelector('[data-map-container]');

      // Show loading indicator during marker fetch.
      if (mapContainer) {
        mapContainer.style.visibility = 'hidden';
      }
      if (loadingIndicator) {
        loadingIndicator.style.display = 'block';
        loadingIndicator.textContent = 'Loading markers...';
      }

      fetch(restUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error('HTTP error! status: ' + response.status);
          }
          return response.json();
        })
        .then((data) => {
          self.displayMarkers(map, data, paragraphId);

          // Hide loading indicator after markers are displayed.
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
          }

          if (mapContainer) {
            mapContainer.style.visibility = 'visible';
          }
        })
        .catch((error) => {
          // Log error for debugging.
          if (window.console && window.console.error) {
            window.console.error('Failed to load partner data:', error);
          }

          // Show error message in loading indicator.
          if (loadingIndicator) {
            loadingIndicator.textContent = 'Failed to load markers. Please try again.';
            loadingIndicator.style.display = 'block';
          }
        });
    },

    /**
     * Get filtered REST URL with current filter parameters.
     *
     * @param {string} baseUrl - The base REST URL.
     * @param {string} paragraphId - The paragraph ID to scope form selection.
     *
     * @return {string} Filtered REST URL.
     */
    getFilteredRestUrl: function (baseUrl, paragraphId) {
      // Extract filter parameters from exposed form if present.
      // Scope to paragraph-specific form to ensure correct filters are read.
      const exposedForm = paragraphId ?
        document.querySelector(`[data-paragraph-id="${paragraphId}"] form.views-exposed-form`) :
        document.querySelector('form.views-exposed-form');
      if (!exposedForm) {
        return baseUrl;
      }

      const params = new URLSearchParams();

      // Get category filter - try multiple possible field names.
      const categorySelect = exposedForm.querySelector('select[name="category[]"]') ||
                            exposedForm.querySelector('select[name="field_category_main_target_id[]"]');
      if (categorySelect) {
        Array.from(categorySelect.selectedOptions).forEach((option) => {
          if (option.value && option.value !== 'All') {
            params.append('category[]', option.value);
          }
        });
      }

      // Get area filter.
      const areaSelect = exposedForm.querySelector('select[name="area[]"]');
      if (areaSelect) {
        Array.from(areaSelect.selectedOptions).forEach((option) => {
          if (option.value && option.value !== 'All') {
            params.append('area[]', option.value);
          }
        });
      }

      const filterString = params.toString();
      return filterString ? `${baseUrl}&${filterString}` : baseUrl;
    },

    /**
     * Spread markers that share identical coordinates into a small circle.
     *
     * Partners at the exact same lat/lng are arranged evenly around a tiny
     * circle (~25 m radius) so every pin is independently clickable.
     *
     * @param {Array} data - Partner data from REST endpoint.
     * @return {Map} Map from partner object to adjusted google.maps.LatLng (duplicates only).
     */
    spreadOverlappingMarkers: function (data) {
      const OFFSET_RADIUS = 0.00003; // ~3 m — visible separation without distorting location.
      const PROXIMITY_THRESHOLD = 0.000009; // ~1 m — markers closer than this appear at the same map position and must be spread.
      const groups = [];

      data.forEach((partner) => {
        const pos = this.parseWktPoint(partner.field_location);
        if (!pos) {
          return;
        }

        // Find an existing group whose reference point is within the proximity threshold.
        let foundGroup = null;
        for (let g = 0; g < groups.length; g++) {
          const ref = groups[g][0].pos;
          if (Math.abs(pos.lat() - ref.lat()) < PROXIMITY_THRESHOLD &&
              Math.abs(pos.lng() - ref.lng()) < PROXIMITY_THRESHOLD) {
            foundGroup = groups[g];
            break;
          }
        }

        if (foundGroup) {
          foundGroup.push({partner, pos});
        }
        else {
          groups.push([{partner, pos}]);
        }
      });

      const adjustments = new Map();
      groups.forEach((group) => {
        if (group.length <= 1) {
          return;
        }
        // Spread around the centroid so all pins stay near their true location.
        const centerLat = group.reduce((sum, {pos}) => sum + pos.lat(), 0) / group.length;
        const centerLng = group.reduce((sum, {pos}) => sum + pos.lng(), 0) / group.length;
        group.forEach(({partner}, i) => {
          const angle = (2 * Math.PI * i) / group.length;
          adjustments.set(partner, new google.maps.LatLng(
            centerLat + OFFSET_RADIUS * Math.cos(angle),
            centerLng + OFFSET_RADIUS * Math.sin(angle)
          ));
        });
      });

      return adjustments;
    },

    /**
     * Display markers on the map.
     *
     * @param {google.maps.Map} map - The Google Maps instance.
     * @param {Array} data - Partner data from REST endpoint.
     * @param {string} paragraphId - The paragraph ID.
     */
    displayMarkers: function (map, data, paragraphId) {
      const self = this;

      // Clear existing markers.
      if (self.markers[paragraphId]) {
        self.markers[paragraphId].forEach(marker => marker.setMap(null));
      }
      self.markers[paragraphId] = [];

      // Clear existing clusterer.
      if (self.markerClusterers[paragraphId]) {
        self.markerClusterers[paragraphId].clearMarkers();
      }

      // Initialize InfoWindows array for this paragraph.
      if (!self.infoWindows[paragraphId]) {
        self.infoWindows[paragraphId] = [];
      }

      // Clear active marker reference.
      if (self.activeMarkers[paragraphId]) {
        delete self.activeMarkers[paragraphId];
      }

      // Pre-compute adjusted positions for partners that share the same lat/lng.
      const positionAdjustments = self.spreadOverlappingMarkers(data);

      // Create bounds to fit all markers.
      const bounds = new google.maps.LatLngBounds();
      const newMarkers = [];

      // Create icon configurations for normal and active states.
      const normalIcon = {
        url: self.MARKER_CONFIG.normal.url,
        scaledSize: new google.maps.Size(
          self.MARKER_CONFIG.normal.size.width,
          self.MARKER_CONFIG.normal.size.height
        ),
        anchor: new google.maps.Point(
          self.MARKER_CONFIG.normal.anchor.x,
          self.MARKER_CONFIG.normal.anchor.y
        )
      };

      const activeIcon = {
        url: self.MARKER_CONFIG.active.url,
        scaledSize: new google.maps.Size(
          self.MARKER_CONFIG.active.size.width,
          self.MARKER_CONFIG.active.size.height
        ),
        anchor: new google.maps.Point(
          self.MARKER_CONFIG.active.anchor.x,
          self.MARKER_CONFIG.active.anchor.y
        )
      };

      // Create markers.
      data.forEach((partner) => {
        const position = positionAdjustments.get(partner) || self.parseWktPoint(partner.field_location);
        if (!position) {
          return;
        }

        const marker = new google.maps.Marker({
          position: position,
          map: map,
          title: self.decodeEntities(partner.title),
          icon: normalIcon
        });

        // Create InfoWindow for this marker.
        const infoWindow = self.createInfoWindow(partner, paragraphId);

        // Store InfoWindow reference.
        self.infoWindows[paragraphId].push(infoWindow);

        // Add click handler with active state management.
        marker.addListener('click', () => {
          // Track analytics event for map marker click.
          if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
              'event': 'partner_click_map_marker_icon',
              'data-analytics-partner-name': self.decodeEntities(partner.title),
              'data-analytics-click-url': window.location.pathname
            });
          }

          // Toggle off if clicking already-active marker.
          if (self.activeMarkers[paragraphId] === marker) {
            self.deactivateMarker(marker, infoWindow, normalIcon, paragraphId);
            return;
          }

          // Reset previously active marker.
          if (self.activeMarkers[paragraphId]) {
            self.deactivateMarker(
              self.activeMarkers[paragraphId],
              null,
              normalIcon,
              paragraphId
            );
          }

          // Close all InfoWindows.
          self.closeAllInfoWindows(paragraphId);

          // Activate clicked marker.
          self.activateMarker(marker, infoWindow, activeIcon, position, map, paragraphId);
        });

        // Reset marker when InfoWindow is closed.
        infoWindow.addListener('closeclick', () => {
          self.deactivateMarker(marker, null, normalIcon, paragraphId);
        });

        newMarkers.push(marker);
        bounds.extend(position);
      });

      // Store markers.
      self.markers[paragraphId] = newMarkers;

      // Handle no results scenario.
      if (newMarkers.length === 0) {
        self.showNoResultsMessage(paragraphId);
      }
      else {
        self.hideNoResultsMessage(paragraphId);

        // Create marker clusterer with custom renderer.
        /* global markerClusterer */
        if (typeof markerClusterer !== 'undefined') {
          const renderer = self.createClusterRenderer();
          self.markerClusterers[paragraphId] = new markerClusterer.MarkerClusterer({
            map: map,
            markers: newMarkers,
            renderer: renderer,
            algorithm: new markerClusterer.SuperClusterAlgorithm({
              radius: self.MAP_DEFAULTS.clusterRadius,
              maxZoom: self.MAP_DEFAULTS.maxZoom - 1
            })
          });
        }

        // Fit bounds to show all markers.
        map.fitBounds(bounds);
      }

      // Update map footer with result count.
      const mapFooter = document.querySelector(`[data-paragraph-id="${paragraphId}"] [data-map-footer]`);
      if (mapFooter) {
        const resultText = newMarkers.length === 1 ? '1 <span>Result</span>' : `${newMarkers.length} <span>Results</span>`;
        mapFooter.innerHTML = resultText;
      }
    },

    /**
     * Parse WKT POINT string to LatLng object.
     *
     * @param {string} wkt - WKT POINT string (e.g., "POINT(-86.5804 35.5175)").
     *
     * @return {google.maps.LatLng|null} LatLng object or null if invalid.
     */
    parseWktPoint: function (wkt) {
      if (!wkt || typeof wkt !== 'string') {
        return null;
      }

      const match = wkt.match(/POINT\s*\(\s*([0-9.-]+)\s+([0-9.-]+)\s*\)/i);
      if (!match) {
        return null;
      }

      const lng = parseFloat(match[1]);
      const lat = parseFloat(match[2]);

      if (isNaN(lat) || isNaN(lng)) {
        return null;
      }

      return new google.maps.LatLng(lat, lng);
    },

    /**
     * Decode HTML entities in a REST field value for plain-text use.
     *
     * Views escapes field output (e.g. "&amp;"), which shows literally when
     * used as text instead of HTML (marker tooltip, analytics).
     *
     * @param {string} value - HTML-escaped string.
     *
     * @return {string} Decoded plain text.
     */
    decodeEntities: function (value) {
      if (!value) {
        return '';
      }

      return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
    },

    /**
     * Show no-results message when map has no markers.
     *
     * @param {string} paragraphId - The paragraph ID.
     */
    showNoResultsMessage: function (paragraphId) {
      const mapContainer = document.querySelector(`[data-paragraph-id="${paragraphId}"] [data-map-container]`);
      const noResultsMessage = document.querySelector(`[data-paragraph-id="${paragraphId}"] [data-map-no-results]`);

      if (mapContainer) {
        mapContainer.style.display = 'none';
      }

      if (noResultsMessage) {
        noResultsMessage.removeAttribute('hidden');
      }
    },

    /**
     * Hide no-results message when map has markers.
     *
     * @param {string} paragraphId - The paragraph ID.
     */
    hideNoResultsMessage: function (paragraphId) {
      const mapContainer = document.querySelector(`[data-paragraph-id="${paragraphId}"] [data-map-container]`);
      const noResultsMessage = document.querySelector(`[data-paragraph-id="${paragraphId}"] [data-map-no-results]`);

      if (mapContainer) {
        mapContainer.style.display = 'block';
      }

      if (noResultsMessage) {
        noResultsMessage.setAttribute('hidden', '');
      }
    },

    /**
     * Create InfoWindow for marker with HTMX support and responsive sizing.
     *
     * @param {Object} partner - Partner data.
     * @param {string} paragraphId - The paragraph ID.
     *
     * @return {google.maps.InfoWindow} The InfoWindow instance.
     */
    createInfoWindow: function (partner, paragraphId) {
      const self = this;
      const isMobile = window.innerWidth < self.BREAKPOINTS.mobile;
      const maxWidth = isMobile ? 360 : 400;

      const infoWindow = new google.maps.InfoWindow({
        content: `<div class="partner-map-popup">${partner.rendered_entity || `<h3>${partner.title}</h3>`}</div>`,
        maxWidth: maxWidth,
        disableAutoPan: true
      });

      // Initialize HTMX attributes when InfoWindow content is ready.
      google.maps.event.addListener(infoWindow, 'domready', () => {
        if (typeof window.htmx !== 'undefined') {
          const infoWindowContent = document.querySelector('.gm-style-iw-d');
          if (infoWindowContent) {
            window.htmx.process(infoWindowContent);
          }
        }

        // Track partner card clicks in map popup.
        const partnerCardButton = document.querySelector('.partner-map-popup .partner-card__link-overlay');
        if (!partnerCardButton) {
          return;
        }

        partnerCardButton.addEventListener('click', () => {
          const eventName = partnerCardButton.getAttribute('data-analytics-event');

          if (eventName !== 'partner_click_map_partner_expand') {
            return;
          }

          const partnerName = partnerCardButton.getAttribute('data-analytics-partner-name') || '';
          const clickText = partnerCardButton.textContent.trim() || partnerName;

          // eslint-disable-next-line no-unused-expressions
          window.dataLayer?.push({
            event: 'partner_click_map_partner_expand',
            partner_name: partnerName,
            click_text: clickText,
            click_url: window.location.pathname
          });
        });
      });

      return infoWindow;
    },

    /**
     * Activate marker with active icon and InfoWindow.
     *
     * @param {google.maps.Marker} marker - The marker to activate.
     * @param {google.maps.InfoWindow} infoWindow - The InfoWindow to open.
     * @param {object} activeIcon - Active state icon configuration.
     * @param {object} position - Marker position.
     * @param {google.maps.Map} map - The map instance.
     * @param {string} paragraphId - The paragraph ID.
     */
    activateMarker: function (marker, infoWindow, activeIcon, position, map, paragraphId) {
      const self = this;

      // Store as active marker.
      self.activeMarkers[paragraphId] = marker;

      // Set active icon immediately without animation to prevent visual jump.
      marker.setIcon(activeIcon);

      // Open InfoWindow immediately.
      infoWindow.open(map, marker);

      // Pan to marker AFTER icon change and InfoWindow open, without animating the marker itself.
      // Use a small delay to ensure InfoWindow is rendered before panning.
      setTimeout(() => {
        self.panToMarker(map, position);
      }, 50);
    },

    /**
     * Deactivate marker by resetting icon.
     *
     * @param {google.maps.Marker} marker - The marker to deactivate.
     * @param {google.maps.InfoWindow|null} infoWindow - The InfoWindow to close (optional).
     * @param {object} normalIcon - Normal state icon configuration.
     * @param {string} paragraphId - The paragraph ID.
     */
    deactivateMarker: function (marker, infoWindow, normalIcon, paragraphId) {
      const self = this;

      // Reset to normal icon.
      marker.setIcon(normalIcon);

      // Clear active marker reference.
      if (self.activeMarkers[paragraphId] === marker) {
        delete self.activeMarkers[paragraphId];
      }

      // Close InfoWindow if provided.
      if (infoWindow) {
        infoWindow.close();
      }
    },

    /**
     * Pan to marker with responsive behavior.
     *
     * Mobile: Center marker with smooth animation.
     * Desktop: Pan marker toward bottom edge so InfoWindow is visible.
     *
     * @param {google.maps.Map} map - The map instance.
     * @param {object} position - Marker position.
     */
    panToMarker: function (map, position) {
      const self = this;
      const isMobile = window.innerWidth < self.BREAKPOINTS.mobile;

      if (isMobile) {
        // Mobile: Center marker with smooth animation using panTo.
        map.panTo(position);
      }
      else {
        // Desktop: Calculate offset position and pan with smooth animation.
        const container = map.getDiv();
        const containerHeight = container.offsetHeight;
        const projection = map.getProjection();

        if (projection) {
          // Calculate the offset in pixels.
          const offsetY = containerHeight * self.MAP_DEFAULTS.desktopPanOffset;

          // Convert position to pixel coordinates.
          const scale = Math.pow(2, map.getZoom());
          const worldCoordinate = projection.fromLatLngToPoint(position);
          const pixelCoordinate = new google.maps.Point(
            worldCoordinate.x * scale,
            worldCoordinate.y * scale
          );

          // Apply offset.
          const newPixelCoordinate = new google.maps.Point(
            pixelCoordinate.x,
            pixelCoordinate.y - offsetY
          );

          // Convert back to lat/lng.
          const newWorldCoordinate = new google.maps.Point(
            newPixelCoordinate.x / scale,
            newPixelCoordinate.y / scale
          );
          const newPosition = projection.fromPointToLatLng(newWorldCoordinate);

          // Pan with smooth animation.
          map.panTo(newPosition);
        }
        else {
          // Fallback to simple panTo if projection is not available.
          map.panTo(position);
        }
      }
    },

    /**
     * Close all InfoWindows for a paragraph.
     *
     * @param {string} paragraphId - The paragraph ID.
     */
    closeAllInfoWindows: function (paragraphId) {
      const self = this;
      const infoWindows = self.infoWindows[paragraphId];

      if (infoWindows && Array.isArray(infoWindows)) {
        infoWindows.forEach(iw => iw.close());
      }
    },

    /**
     * Create cluster renderer with custom marker styling.
     *
     * @return {object} Renderer object with render method.
     */
    createClusterRenderer: function () {
      const self = this;
      const clusterIcon = {
        url: self.MARKER_CONFIG.cluster.url,
        scaledSize: new google.maps.Size(
          self.MARKER_CONFIG.cluster.size.width,
          self.MARKER_CONFIG.cluster.size.height
        ),
        anchor: new google.maps.Point(
          self.MARKER_CONFIG.cluster.anchor.x,
          self.MARKER_CONFIG.cluster.anchor.y
        )
      };

      return {
        render: ({count, position}) => {
          return new google.maps.Marker({
            position: position,
            icon: clusterIcon,
            label: {
              text: String(count),
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold'
            },
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
          });
        }
      };
    },

    /**
     * Check if AJAX update is related to this paragraph's view.
     *
     * @param {Event} event - The AJAX event.
     * @param {Object} settings - Settings from drupalSettings.
     *
     * @return {boolean} True if related, false otherwise.
     */
    isRelatedView: function (event, settings) {
      // Check if the AJAX update affects our list view.
      return event.detail?.viewId === settings.listViewId &&
             event.detail?.displayId === settings.listDisplayId;
    },

    /**
     * Debounced filter change handler to prevent duplicate map reloads.
     *
     * @param {HTMLElement} paragraph - The paragraph element.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {string} paragraphId - The paragraph ID.
     */
    debouncedFilterChange: function (paragraph, settings, paragraphId) {
      const self = this;

      // Clear existing timeout for this paragraph.
      if (self.filterChangeTimeouts[paragraphId]) {
        clearTimeout(self.filterChangeTimeouts[paragraphId]);
      }

      // Set new timeout for consolidated reload.
      self.filterChangeTimeouts[paragraphId] = setTimeout(() => {
        self.handleFilterChange(paragraph, settings, paragraphId);
        delete self.filterChangeTimeouts[paragraphId];
      }, 500);
    },

    /**
     * Handle filter change to reload map markers.
     *
     * @param {HTMLElement} paragraph - The paragraph element.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {string} paragraphId - The paragraph ID.
     */
    handleFilterChange: function (paragraph, settings, paragraphId) {
      const self = this;
      const map = self.mapInstances[paragraphId];

      if (map) {
        // Reload markers with updated filters.
        self.loadMarkers(map, settings, paragraphId);
      }
    },

    /**
     * Handle AJAX update to reload map markers.
     *
     * @param {HTMLElement} paragraph - The paragraph element.
     * @param {Object} settings - Settings from drupalSettings.
     * @param {string} paragraphId - The paragraph ID.
     */
    handleAjaxUpdate: function (paragraph, settings, paragraphId) {
      const self = this;
      const map = self.mapInstances[paragraphId];

      if (map) {
        // Reload markers with updated filters.
        self.loadMarkers(map, settings, paragraphId);
      }
    }
  };

})(Drupal, drupalSettings);
